import { useDispatch, useSelector } from 'react-redux';
import Routes from '../../constants/routes';
import useChangeFavorite from '../../hooks/use-change-favorite';
import useCheckIsInFavorites from '../../hooks/use-check-is-in-favorites';
import translation from '../../languages';
import useAddToCart from '../../services/cart/use-add-to-cart';
import useCart from '../../services/cart/use-cart';
import useRemoveFromCart from '../../services/cart/use-remove-from-cart';
import { aiStripeEvents } from '../../services/quality-events/ai-stripe-events';
import qualityEvents from '../../services/quality-events/quality-events';
import { CART_REMOVE_FROM_CART_SOURCE } from '../../services/quality-events/quality-events-pages/cart-page-quality-events.constants';
import Button, { ButtonProps } from '../../ui/button/button';
import Spinner from '../../ui/spinner/spinner';
import enhancedEcommerce from '../../utils/enhanced-ecommerce';
import isMaterialInCart from '../../utils/material/is-material-in-cart';
import pubsub from '../../utils/pubsub';
import schoolAccount from '../../utils/school-account';
import session from '../../utils/session';
import { ADD_TO_CART_MODAL_TYPES } from '../add-to-cart-modal/add-to-cart-modal.constants';
import Icon from '../icon/icon';
import { AddToCartButtonType, CartIconName } from './add-to-cart-button.types';
import './add-to-cart-button.scss';


export type AddToCartButtonProps = TranslationProps & {
    material: Material;
    alreadyInCartText?: string;
    className?: string;
    text?: string;
    title?: string;
    type?: AddToCartButtonType;
    cartIconName?: CartIconName;
    onAddToCartCallback?: (material: Material) => boolean;
    materialIndex?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    qualityEventsExtra?: any;
    onAddToCartSuccess?: (props: { cart: Cart; idsOfMaterialsIncludedInBundle: number[] }) => void;
    onAddToCartError?: VoidFunction;
    isDisabled?: boolean;
    shouldPreventCartOpen?: boolean;
    addToCartData?: { material: Array<{ id: number; purchaseType: number }> };
    scrollToTopOnSuccess?: boolean;
    hideRestrictionsMessage?: boolean;
    areAllMaterialsInCart?: boolean;
    size?: ButtonProps['size'];
    searchRequestId?: string;
    shouldSyncCartOnSuccess?: boolean;
};

const AddToCartButton = (props: AddToCartButtonProps) => {
    const {
        material,
        materialIndex,
        t,
        title = '',
        className = '',
        size,
        text = t('common.materialBlock.addToCart'),
        alreadyInCartText = t('common.materialBlock.alreadyInCart'),
        type = 'button',
        cartIconName = 'cart',
        onAddToCartCallback,
        qualityEventsExtra,
        onAddToCartSuccess,
        onAddToCartError,
        isDisabled,
        shouldPreventCartOpen,
        addToCartData,
        scrollToTopOnSuccess,
        hideRestrictionsMessage,
        areAllMaterialsInCart,
        searchRequestId,
        shouldSyncCartOnSuccess = true
    } = props;

    const checkIsInFavorites = useCheckIsInFavorites();
    const { data: cart } = useCart();
    const user = useSelector((state: RootState) => state.main.user);
    const dispatch = useDispatch();
    const { changeFavoriteItem } = useChangeFavorite();
    const materialAlreadyInCart = areAllMaterialsInCart || isMaterialInCart(material.id, cart);
    const { mutateAsync: addToCartAsync, isPending: isAddingToCart } = useAddToCart({ shouldSyncCartOnSuccess });
    const { mutate: removeFromCartMutate } = useRemoveFromCart();

    const removeItem = () => {
        removeFromCartMutate(material.id);

        enhancedEcommerce.tracker.removeFromCart(material);
        qualityEvents.cartPage.removeFromCart({
            item: material,
            itemIndex: materialIndex,
            source: CART_REMOVE_FROM_CART_SOURCE.MATERIAL_CARD,
            searchRequestId
        });
    };

    const addToCart = () => {
        if (isAddingToCart || materialAlreadyInCart) {
            return;
        }

        const materialItem = window.sessionStorage.getItem(`material_${material.id}`);

        aiStripeEvents(materialItem, material.id.toString(), 'AddToCart');

        enhancedEcommerce.tracker.addToCart([{ ...material }]);

        const isFavoritesPage = window.location.pathname.indexOf(Routes.FAVORITES.path) >= 0;
        const from = isFavoritesPage ? 'fromFavorites' : undefined;

        qualityEvents.materialPage.priceBlock.addToCart(
            material,
            materialItem,
            materialIndex,
            from,
            qualityEventsExtra,
            searchRequestId
        );
        onAddToCartCallback && onAddToCartCallback(material);

        const cartData = addToCartData ?? {
            material: {
                id: material.id,
                purchaseType: 1
            }
        };

        // Runs alongside the cart request, so a material added to the cart leaves the favorites without a delay.
        const inFavoritesCheck = checkIsInFavorites(material.id);

        // mutateAsync, not mutate callbacks: callers may unmount this button while the request is in flight
        addToCartAsync(cartData)
            .then((cart) => {
                if (material.interactiveId && !session.isLoggedIn()) {
                    pubsub.publish('showAddToCartModal', { type: ADD_TO_CART_MODAL_TYPES.INTERACTIVE_SUCCESS });
                } else if (!shouldPreventCartOpen) {
                    pubsub.publish('cartSlideDown', {});
                    scrollToTopOnSuccess &&
                        setTimeout(() => {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }, 200);
                }

                inFavoritesCheck.then((inFavorites) => {
                    if (inFavorites) {
                        changeFavoriteItem({
                            item: material,
                            add: false,
                            itemIndex: materialIndex,
                            isApproved: true,
                            searchRequestId
                        });
                    }
                })

                const idsOfMaterialsIncludedInBundle = material.bundleMaterials?.map((material) => material.id) ?? [];

                onAddToCartSuccess?.({ cart, idsOfMaterialsIncludedInBundle });
            })
            .catch(() => {
                onAddToCartError
                    ? onAddToCartError()
                    : pubsub.publish('showAddToCartModal', { type: ADD_TO_CART_MODAL_TYPES.ERROR });
            });
    };

    const goToCartWhenDisabled = () => {
        qualityEvents.materialPage.priceBlock.addToCartInactive(qualityEventsExtra);
        window.location.href = `${Routes.WARENKORB.path}`;
    };

    const handleClick = async (): Promise<void> => {
        const shouldAllowClick = !onAddToCartCallback || (await onAddToCartCallback(material));

        if (!shouldAllowClick) {
            onAddToCartError?.();

            return;
        }

        if (materialAlreadyInCart) {
            removeItem();
        } else {
            addToCart();
        }
    };

    const isButtonDisabled = (schoolAccount.isAdmin(user) && material.hasFixedPrice) || isDisabled || isAddingToCart;

    return (
        <>
            {['button', 'disableable-button'].includes(type) && (
                <>
                    {!materialAlreadyInCart && (
                        <>
                            <Button
                                disabled={isButtonDisabled}
                                title={title}
                                variant={`add-to-cart-btn ${className}`}
                                size={size}
                                onClick={handleClick}
                            >
                                {isAddingToCart ? <Spinner size={25} /> : <>{text}</>}
                            </Button>
                            {isButtonDisabled && !hideRestrictionsMessage && (
                                <div className='mt-2 mb-2'>{t('common.materialBlock.restrictedMaterialForSchool')}</div>
                            )}
                        </>
                    )}
                    {materialAlreadyInCart && (
                        <Button
                            title={title}
                            disabled={type === 'disableable-button'}
                            variant={`add-to-cart-btn disabled ${className}`}
                            size={size}
                            onClick={goToCartWhenDisabled}
                        >
                            <>{alreadyInCartText}</>
                        </Button>
                    )}
                </>
            )}
            {type === 'icon' && (
                <>
                    {isAddingToCart ? (
                        <span className='cart-icon'>
                            <Spinner size={25} />
                        </span>
                    ) : (
                        <Icon
                            active={materialAlreadyInCart}
                            width={26}
                            name={cartIconName}
                            onClick={handleClick}
                            disabled={isButtonDisabled}
                        />
                    )}
                </>
            )}
            {type === 'icon-button' && (
                <>
                    {!materialAlreadyInCart && (
                        <>
                            <Button
                                disabled={isButtonDisabled}
                                title={title}
                                variant={`add-to-cart-btn ${className}`}
                                size={size}
                                onClick={handleClick}
                            >
                                <>
                                    {!isAddingToCart && (
                                        <Icon className='mr-3' active={materialAlreadyInCart} name={cartIconName} />
                                    )}
                                    {!isAddingToCart && <>{text}</>}
                                    {isAddingToCart && <Spinner size={25} />}
                                </>
                            </Button>
                            {isButtonDisabled && !hideRestrictionsMessage && (
                                <div className='mt-2 mb-2'>{t('common.materialBlock.restrictedMaterialForSchool')}</div>
                            )}
                        </>
                    )}
                    {materialAlreadyInCart && (
                        <Button
                            disabled={isButtonDisabled}
                            title={title}
                            variant={`add-to-cart-btn disabled ${className}`}
                            size={size}
                            onClick={goToCartWhenDisabled}
                        >
                            <>
                                {!isAddingToCart && (
                                    <Icon className='mr-3' active={materialAlreadyInCart} name={cartIconName} />
                                )}
                                {alreadyInCartText}
                            </>
                        </Button>
                    )}
                </>
            )}
        </>
    );
};

const translate = translation('common');

export default translate(AddToCartButton);
